/**
 * Central PERMISSIONS registry for APFv2 frontend.
 * These strings MUST exactly match the `name` column in the DB `permissions` table.
 * DO NOT use raw strings in components — always import from here.
 */

export const PERMISSIONS = {
  // ── Page Access ──────────────────────────────────────────────────────────
  PAGE_HOME:           'page:home:view',
  PAGE_TASK_CENTER:    'page:task_center:view',
  PAGE_EXEC_STATUS:    'page:execution_status:view',
  PAGE_WORKSPACE:      'page:workspace:view',
  PAGE_KB:             'page:knowledge_base:view',
  PAGE_CREDITS:        'page:credits:view',
  PAGE_DASHBOARD:      'page:dashboard:view',
  PAGE_TENANTS:        'page:tenants:view',
  PAGE_PARTNERS:       'page:partners:view',
  PAGE_USERS:          'page:users:view',
  PAGE_ROLES:          'page:roles_permissions:view',
  PAGE_PLANS:          'page:plans:view',
  PAGE_SETTINGS:       'page:settings:view',
  PAGE_SCHEDULER:      'page:scheduler:view',
  PAGE_ALERTS:         'page:alerts:view',
  PAGE_SUPPORT:        'page:support:view',
  PAGE_FLOWS:          'page:flows:view',
  PAGE_AI_INSIGHT:     'page:agentic_insight:view',
  PAGE_APPS:           'page:business_apps:view',
  PAGE_ML:             'page:contents:view',
  PAGE_DB_STUDIO:      'page:dashboard_studio:view',
  PAGE_DATAMODELS:     'page:datamodels:view',
  PAGE_DATASETS:       'page:datasets:view',
  PAGE_CONNECTIONS:    'page:connections:view',
  PAGE_DATASOURCES:    'page:datasources:view',
  PAGE_DQ_PROFILER:    'page:dq_profiler:view',

  // ── Tenant Portal Pages ───────────────────────────────────────────────────
  PAGE_T_ALERTS:      'page:tenant:alerts:view',
  PAGE_T_APPS:        'page:tenant:apps:view',
  PAGE_T_BYOM:        'page:tenant:byom:view',
  PAGE_T_BYOR:        'page:tenant:byor:view',
  PAGE_T_CONNECTIONS: 'page:tenant:connections:view',
  PAGE_T_DATASOURCES: 'page:tenant:datasources:view',
  PAGE_T_DX:          'page:tenant:data_explorer:view',
  PAGE_T_SCHEDULER:   'page:tenant:scheduler:view',
  PAGE_T_SETTINGS:    'page:tenant:settings:view',
  PAGE_T_USERS:       'page:tenant:users:view',

  // ── Tasks & Executions ────────────────────────────────────────────────────
  TASKS_VIEW_ALL:    'tasks:view_all',
  TASKS_MANAGE:      'tasks:manage',
  EXECS_VIEW_ALL:    'executions:view_all',
  EXECS_MANAGE:      'executions:manage',

  // ── Flows ─────────────────────────────────────────────────────────────────
  FLOWS_PUBLISH:   'flows:publish',
  FLOWS_SHARE:     'flows:share',
  FLOWS_SCHEDULE:  'flows:schedule',
  FLOWS_IMPORT:    'flows:import',
  FLOWS_EXPORT:    'flows:export',

  // ── Agentic Insights ───────────────────────────────────────────────────────────
  AI_VIEW:             'ai_insights:view',
  AI_SWITCH_MODEL:     'ai_insights:switch_model',
  AI_PIN:              'ai_insights:pin',
  AI_EXECUTE:          'ai_insight:execute',
  AI_EXPORT:           'ai_insight:export',
  AI_SHARE:            'ai_insight:share',
  AI_VIEW_ALL_SESSIONS:'ai_insight:view_all_sessions',

  // ── Dashboard ─────────────────────────────────────────────────────────────
  DASHBOARD_CREATE:       'dashboard:create',
  DASHBOARD_EDIT:         'dashboard:edit',
  DASHBOARD_DELETE:       'dashboard:delete',
  DASHBOARD_PUBLISH:      'dashboard:publish',
  DASHBOARD_SHARE:        'dashboard:share',
  DASHBOARD_EXPORT:       'dashboard:export',
  DASHBOARD_SCHEDULE:     'dashboard:schedule',
  DASHBOARD_ARCHIVE:      'dashboard:archive',
  DASHBOARD_ASSIGN_TENANT:'dashboard:assign_tenant',

  // ── Business Apps ─────────────────────────────────────────────────────────
  BUNDLES_MANAGE:       'bundles:manage',
  APPS_CREATE:          'apps:create',
  APPS_PUBLISH:         'apps:publish',
  APPS_ARCHIVE:         'apps:archive',
  APPS_ASSIGN_PARTNER:  'apps:assign_partner',
  APPS_ASSIGN_TENANT:   'apps:assign_tenant',
  APPS_UNASSIGN_TENANT: 'apps:unassign_tenant',
  APPS_IMPORT:          'apps:import',
  APPS_EXPORT:          'apps:export',

  // ── Content (ML Studio) ───────────────────────────────────────────────────
  CONTENT_VIEW:         'content:view',
  CONTENT_MANAGE:       'content:manage',
  CONTENT_EXECUTE:      'content:execute',
  CONTENT_PUBLISH:      'content:publish',
  CONTENT_ARCHIVE:      'content:archive',
  CONTENT_SHARE:        'content:share',
  CONTENT_EXPORT:       'content:export',
  CONTENT_ASSIGN_TENANT:'content:assign_tenant',

  // ── Workspace & Data Explorer ─────────────────────────────────────────────
  WORKSPACE_MANAGE:    'workspace:manage',
  WORKSPACE_VIEW_ALL:  'workspace:view_all',
  DATA_EXPLORER_DROP:  'data_explorer:drop',
  DATA_EXPLORER_VIEW:  'data_explorer:view',

  // ── Connections & Data Sources ────────────────────────────────────────────
  CONNECTIONS_MANAGE_GLOBAL: 'connections:manage_global',
  DATASOURCES_CREATE:  'datasources:create',
  DATASOURCES_EDIT:    'datasources:edit',
  DATASOURCES_DELETE:  'datasources:delete',
  DATASOURCES_TEST:    'datasources:test',
  DATASOURCES_EXPORT:  'datasources:export',

  // ── Data Models ───────────────────────────────────────────────────────────
  DATASETS_VIEW:         'datasets:view',
  DATA_MODELS_VIEW:      'data_models:view',
  DATA_MODELS_MANAGE:    'data_models:manage',
  DATA_MODELS_REFRESH:   'data_models:refresh',
  DATA_MODELS_IMPORT:    'data_models:import',
  DATA_MODELS_EXPORT:    'data_models:export',
  DATA_MODELS_ARCHIVE:   'data_models:archive',
  DATA_MODELS_ASSIGN:    'data_models:assign_tenant',
  DATA_MODELS_SHARE:     'data_models:share',

  // ── Knowledge Base ────────────────────────────────────────────────────────
  KB_VIEW:    'knowledge_base:view',
  KB_CREATE:  'knowledge_base:create',
  KB_EDIT:    'knowledge_base:edit',
  KB_DELETE:  'knowledge_base:delete',
  KB_MANAGE:  'knowledge_base:manage',
  KB_IMPORT:  'knowledge_base:import',
  KB_EXPORT:  'knowledge_base:export',
  KB_PUBLISH: 'knowledge_base:publish',
  KB_ARCHIVE: 'knowledge_base:archive',
  KB_ASSIGN:  'knowledge_base:assign_tenant',

  // ── Scheduler ─────────────────────────────────────────────────────────────
  SCHEDULER_MANAGE:  'scheduler:manage',
  SCHEDULER_VIEW:    'scheduler:view',
  SCHEDULER_TRIGGER: 'scheduler:trigger',
  SCHEDULER_EXPORT:  'scheduler:export',

  // ── Alerts ────────────────────────────────────────────────────────────────
  ALERTS_VIEW:        'alerts:view',
  ALERTS_MANAGE:      'alerts:manage',
  ALERTS_ACKNOWLEDGE: 'alerts:acknowledge',
  ALERTS_EXPORT:      'alerts:export',

  // ── Partners ──────────────────────────────────────────────────────────────
  PARTNERS_VIEW:          'partners:view',
  PARTNERS_MANAGE:        'partners:manage',
  PARTNERS_CREATE:        'partners:create',
  PARTNERS_DELETE:        'partners:delete',
  PARTNERS_ASSIGN_TENANT: 'partners:assign_tenant',
  PARTNERS_BILLING:       'partners:view_billing',
  PARTNERS_EXPORT:        'partners:export',

  // ── Tenants ───────────────────────────────────────────────────────────────
  TENANTS_VIEW_ALL:         'tenants:view_all',
  TENANTS_MANAGE_ALL:       'tenants:manage_all',
  TENANTS_APPROVE:          'tenants:approve',
  TENANTS_ASSIGN_APPS:      'tenants:assign_apps',
  TENANTS_CREATE:           'tenants:create',
  TENANTS_DELETE:           'tenants:delete',
  TENANTS_SUSPEND:          'tenants:suspend',
  TENANTS_ASSIGN_PLAN:      'tenants:assign_plan',
  TENANTS_BILLING:          'tenants:view_billing',
  TENANTS_EXPORT:           'tenants:export',
  TENANTS_ONBOARDING_VIEW:  'tenants:onboarding_view',
  TENANTS_ONBOARDING_MANAGE:'tenants:onboarding_manage',

  // ── Users ─────────────────────────────────────────────────────────────────
  USERS_VIEW:        'users:view',
  USERS_MANAGE:      'users:manage',
  USERS_CREATE:      'users:create',
  USERS_DELETE:      'users:delete',
  USERS_ACTIVATE:    'users:activate',
  USERS_ASSIGN_ROLE: 'users:assign_role',
  USERS_EXPORT:      'users:export',
  USERS_IMPERSONATE: 'users:impersonate',

  // ── Roles & Permissions ───────────────────────────────────────────────────
  ROLES_VIEW:              'roles:view',
  ROLES_ASSIGN_PERMISSIONS:'roles:assign_permissions',
  ROLES_MANAGE_DEFINITIONS:'roles:manage_definitions',
  ROLES_MANAGE_SYSTEM:     'roles:manage_system',
  ROLES_MANAGE_CUSTOM:     'roles:manage_custom',
  ROLES_EXPORT:            'roles:export',

  // ── Plans ─────────────────────────────────────────────────────────────────
  PLANS_MANAGE:       'plans:manage',
  PLANS_VIEW:         'plans:view',
  PLANS_ASSIGN_TENANT:'plans:assign_tenant',
  PLANS_EXPORT:       'plans:export',

  // ── Credits ───────────────────────────────────────────────────────────────
  CREDITS_VIEW:   'credits:view',
  CREDITS_MANAGE: 'credits:manage',
  CREDITS_ADJUST: 'credits:adjust',
  CREDITS_EXPORT: 'credits:export',

  // ── Support ───────────────────────────────────────────────────────────────
  SUPPORT_VIEW:     'support:view',
  SUPPORT_ASSIGN:   'support:assign',
  SUPPORT_REPLY:    'support:reply',
  SUPPORT_RESOLVE:  'support:resolve',
  SUPPORT_CLOSE:    'support:close',
  SUPPORT_REASSIGN: 'support:reassign',
  SUPPORT_ESCALATE: 'support:escalate',
  SUPPORT_ARCHIVE:  'support:archive',
  SUPPORT_EXPORT:   'support:export',

  // ── Settings & Relay Agents ───────────────────────────────────────────────
  SETTINGS_PLATFORM_EDIT:       'settings:edit_platform',
  SETTINGS_INTEGRATIONS_GLOBAL: 'settings:integrations_global',
  SETTINGS_INTEGRATIONS:        'settings:manage_integrations',
  SETTINGS_BRANDING:            'settings:manage_branding',
  SETTINGS_SECURITY:            'settings:manage_security',
  SETTINGS_API_KEYS:            'settings:manage_api_keys',
  RELAY_AGENTS_VIEW:            'relay_agents:view',
  RELAY_AGENTS_MANAGE:          'relay_agents:manage',
  RELAY_AGENTS_TOKEN:           'relay_agents:manage_token',
};
